import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axios";
import SignTableDocSingle from "../approval/SignTable_doc_single";

export default function MaterialCKView({ data }) {
  const navigate = useNavigate();
  const { id: projectId } = useParams();

  /* ===============================
     로그인 유저
  =============================== */
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const myId = String(user.id);

  /* ===============================
     STATE
  =============================== */
  const [rowsState, setRowsState] = useState([]);
  const [saving, setSaving] = useState(false);
  const [finalApproverId, setFinalApproverId] = useState(null);

  /* ===============================
     기본 데이터 (data 없어도 Hook 순서 유지용)
  =============================== */
  const header = data?.header || {};
  const dns = data?.dns || data?.doc_dns;
  const qcDns = header.qc_dns || data?.qc_dns || header.parent_dns || null;

  /* ===============================
     rows 초기화
  =============================== */
  useEffect(() => {
    if (!data?.lines) return;
    const rows = data.lines.map((r) => r.body || r);
    setRowsState(rows);
  }, [data]);

  /* ===============================
     유효 행
  =============================== */
  const validRows = useMemo(
    () => rowsState.filter((r) => r.item || r.criteria || r.action),
    [rowsState]
  );

  const isPmChecklistComplete = useMemo(() => {
    if (!validRows.length) return false;
    return validRows.every((r) => r.pm_ok === true || r.pm_ng === true);
  }, [validRows]);

  /* ===============================
     담당자 (PM)
  =============================== */
  const pmId = header.assigned_people?.[1]
    ? String(header.assigned_people[1])
    : null;

  const isPM = myId === pmId;

  /* ===============================
     문서 상태
  =============================== */
  const isApproved = data?.status === "approved";
  const isRejected = data?.status === "rejected";

  /* ===============================
     마지막 승인자 조회
  =============================== */
  useEffect(() => {
    if (!dns) return;

    (async () => {
      try {
        const res = await api.get("approval/lines/view", {
          params: { dns },
        });

        const approvedLines = (res.data?.approval_lines || [])
          .filter((l) => l.is_approved === "approved")
          .sort((a, b) => b.order - a.order);

        if (approvedLines.length) {
          setFinalApproverId(String(approvedLines[0].approver_id));
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [dns]);

  /* ===============================
     PM 체크 변경
  =============================== */
  const updatePmCheck = (idx, key) => {
    setRowsState((prev) =>
      prev.map((r, i) =>
        i === idx ? { ...r, pm_ok: key === "pm_ok", pm_ng: key === "pm_ng" } : r
      )
    );
  };

  /* ===============================
     체크리스트 저장
  =============================== */
  const saveChecklist = async () => {
    const payload = {
      values: {
        project: projectId,
        module: "material",
        doc_type: "CK",
        title: data?.title,
        header,
        lines: rowsState,
        level2: "APR",
        dns,
      },
    };

    await api.post("material/docs/save_full/", payload);
  };

  /* ===============================
     PM 최종 승인
  =============================== */
  const approveByPM = async () => {
    if (!isPmChecklistComplete) {
      alert("PM 체크리스트를 모두 확인해주세요.");
      return;
    }

    try {
      setSaving(true);

      // ⭐ 1. 체크리스트 저장
      console.log("📝 체크리스트 저장 중...");
      await saveChecklist();

      // ⭐ 2. 불합격 항목 확인
      const hasFailure = validRows.some((r) => r.pm_ng === true);
      console.log("🔍 불합격 항목 확인:", { hasFailure, qcDns, dns, validRows });

      if (hasFailure) {
        // 🔥 PM 불합격 시 QC와 CK 모두 반려 처리
        console.log("🚨 PM 불합격 → QC + CK 모두 반려 처리");

        // 1. QC 문서 반려
        if (qcDns) {
          console.log(`🚨 QC 문서 반려 처리: ${qcDns}`);
          await api.patch(`papers/docs/${qcDns}/status/`, {
            status: "rejected",
          });
          console.log("✅ QC 문서 반려 완료");
        }

        // 2. CK 문서도 반려
        if (dns) {
          console.log(`🚨 CK 문서 반려 처리: ${dns}`);
          await api.patch(`papers/docs/${dns}/status/`, {
            status: "rejected",
          });
          console.log("✅ CK 문서 반려 완료");
        }

        alert("PM 불합격 항목이 있어 검수 요청서와 체크리스트가 반려되었습니다.\n시공사가 수정하여 재요청할 수 있습니다.");
        window.location.reload();
        return;
      }

      // ⭐ 3. 모두 합격 → QC와 CK 문서 모두 승인 처리
      console.log("✅ 모두 합격 - QC 및 CK 승인 처리 시작");

      // 3-1. QC 문서 승인
      if (qcDns) {
        console.log(`✅ QC 문서 승인 처리: ${qcDns}`);
        await api.patch(`papers/docs/${qcDns}/status/`, {
          status: "approved",
        });
        console.log("✅ QC 문서 승인 완료");
      }

      // 3-2. CK 문서 승인 (결재 처리)
      const lineRes = await api.get("approval/lines/view", {
        params: { dns },
      });

      const myLine = (lineRes.data?.approval_lines || []).find(
        (l) => String(l.approver_id) === myId && l.is_approved === "submit"
      );

      if (!myLine) {
        alert("처리 가능한 결재 라인이 없습니다.");
        return;
      }

      await api.patch(
        `approval/action/${myLine.id}/`,
        { action: "approved" },
        { params: { project: projectId } }
      );

      console.log("✅ CK 결재 승인 완료");

      alert("PM 최종 승인 완료\n검수 요청서와 체크리스트가 모두 승인되었습니다.");
      window.location.reload();
    } catch (e) {
      console.error("❌ PM 승인 실패:", e);
      console.error("에러 상세:", e.response?.data || e.message);
      alert(`PM 승인 실패: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
     QC 결과 이동
  =============================== */
  const goToQCResult = () => {
    if (!qcDns) {
      alert("연결된 QC 문서를 찾을 수 없습니다.");
      return;
    }
    navigate(`/project/${projectId}/material/request/${qcDns}/view`);
  };

  /* ===============================
     data 없는 경우는 JSX에서 처리
  =============================== */
  if (!data) {
    return <div className="p-4 text-gray-400">데이터 없음</div>;
  }

  return (
    <div className="mt-10 space-y-4">
      {isApproved && String(myId) === String(finalApproverId) && (
        <div className="flex justify-end mb-4">
          <button className="btn-basic-sm" onClick={goToQCResult}>
            자재 검수 결과 통보서 작성하기
          </button>
        </div>
      )}

      <div className="text-body-bold">검사 항목</div>

      <table className="doc-table w-full text-sm">
        <thead className="doc-thead">
          <tr>
            <th className="doc-th">검사항목</th>
            <th className="doc-th">검사기준</th>
            <th className="doc-th">시공사 합격</th>
            <th className="doc-th">시공사 불합격</th>
            <th className="doc-th">PM 합격</th>
            <th className="doc-th">PM 불합격</th>
            <th className="doc-th-end">조치사항</th>
          </tr>
        </thead>

        <tbody>
          {validRows.map((r, idx) => (
            <tr key={idx}>
              <td className="doc-td">{r.item}</td>
              <td className="doc-td">{r.criteria}</td>
              <td className="doc-td text-center">{r.s_ok ? "✓" : ""}</td>
              <td className="doc-td text-center">{r.s_ng ? "✓" : ""}</td>

              <td className="doc-td text-center">
                {isPM && !isApproved && !isRejected ? (
                  <input
                    type="checkbox"
                    checked={r.pm_ok}
                    onChange={() => updatePmCheck(idx, "pm_ok")}
                  />
                ) : r.pm_ok ? (
                  "✓"
                ) : (
                  ""
                )}
              </td>

              <td className="doc-td text-center">
                {isPM && !isApproved && !isRejected ? (
                  <input
                    type="checkbox"
                    checked={r.pm_ng}
                    onChange={() => updatePmCheck(idx, "pm_ng")}
                  />
                ) : r.pm_ng ? (
                  "✓"
                ) : (
                  ""
                )}
              </td>

              <td className="doc-td">
                {isPM && !isApproved && !isRejected ? (
                  <textarea
                    className="input-sm w-full"
                    value={r.action || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRowsState((prev) =>
                        prev.map((row, i) =>
                          i === idx ? { ...row, action: value } : row
                        )
                      );
                    }}
                  />
                ) : (
                  r.action || "-"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {dns && <SignTableDocSingle dns={dns} />}

      {isPM && !isApproved && !isRejected && (
        <div className="flex justify-end gap-2 mt-4">
          <button
            className="btn-basic-sm"
            disabled={!isPmChecklistComplete || saving}
            onClick={approveByPM}
          >
            최종 승인
          </button>
        </div>
      )}
    </div>
  );
}
